"use client";

import { checkNowPaymentsAndCreateMembership } from "@/lib/nowpayments";
import { checkPaymentAndCreateMembership } from "@/lib/stripe";
import { Box, CircularProgress } from "@mui/material";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

export type PaymentCheckProps = {
  onPaymentComplete?: () => void;
  onPaymentError: (errorCode: string) => void;
};

export default function PaymentCheck({ onPaymentComplete, onPaymentError }: PaymentCheckProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const nowpaymentsPaymentId = searchParams.get("NP_id");
    if (sessionId) {
      checkStatus(sessionId);
    }
    if (nowpaymentsPaymentId) {
      checkNowPayments(nowpaymentsPaymentId);
    }
  }, [searchParams]);

  // 检查stripe支付状态
  const checkStatus = async (sessionId: string) => {
    const checkMembership = async () => {
      try {
        const result = await checkPaymentAndCreateMembership(sessionId);
        if (result.success) {
          window.dataLayer.push({
            event: "purchase",
            value: result.data?.amountTotal,
            currency: "USD",
            transaction_id: result.data?.sessionUniqueId,
          });
          clearUrlSessionId();
          if (onPaymentComplete) {
            setTimeout(() => {
              onPaymentComplete();
            }, 300);
          }
        } else {
          onPaymentError(result?.error?.code || "Server.error");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        onPaymentError("Server.error");
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    await checkMembership();
  };

  function clearUrlSessionId() {
    const url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    const newPath = `${url.pathname}${url.search}`;
    router.replace(newPath, { scroll: true });
  }

  async function checkNowPayments(nowpaymentsPaymentId: string) {
    try {
      setLoading(true);
      const res = await checkNowPaymentsAndCreateMembership(nowpaymentsPaymentId);
      if (res.success) {
        window.dataLayer.push({
          event: "purchase",
          value: res.data?.amountTotal,
          currency: "USD",
          transaction_id: res.data?.sessionUniqueId,
        });
        clearUrlNowPaymentsId();
        if (onPaymentComplete) {
          setTimeout(() => {
            onPaymentComplete();
          }, 300);
        }
      } else {
        onPaymentError(res?.error?.code || "Server.error");
      }
    } catch (error) {
      console.error("Error checking NowPayments status:", error);
      onPaymentError("Server.error");
    } finally {
      setLoading(false);
    }
  }

  function clearUrlNowPaymentsId() {
    const url = new URL(window.location.href);
    url.searchParams.delete("NP_id");
    const newPath = `${url.pathname}${url.search}`;
    router.replace(newPath, { scroll: true });
  }

  if (!loading) return null;

  return (
    <Box sx={{ textAlign: "center", my: 2 }}>
      <CircularProgress />
    </Box>
  );
}
